import type { QueryClient } from "@tanstack/react-query";
import { type AnyRoute, createRoute, lazyRouteComponent, Outlet } from "@tanstack/react-router";
import { chatTokenQueries, inboxQueries } from "domains/messaging/providers/Auth/queries";
import { warmQueries } from "routing/warmQueries";
import * as z from "zod/mini";
import { locationLayoutRoute } from "./locations";
import { rootRoute } from "./root";

// The messaging Home layout mounts under three parents (standalone `chats/$chatToken`, authed `inbox` and
// `hire/inbox`). File-based routing can't mount one subtree twice, so this is a factory, called once per parent.

const homeViews = () => import("domains/messaging/views/Home");
const authProvider = () => import("domains/messaging/providers/Auth");

const Home = lazyRouteComponent(homeViews, "Home");
const ListView = lazyRouteComponent(homeViews, "ListView");
const ChannelView = lazyRouteComponent(homeViews, "ChannelView");

// lazyRouteComponent only skips suspending once its own preload ran, so warm the exact instances that render.
const preloadMessaging = () =>
  void Promise.all([Home.preload?.(), ListView.preload?.(), ChannelView.preload?.(), authProvider()]);

// Hover warms the session token and code only; the socket opens when the provider mounts (Sendbird bills per connection).
export const messagingInboxRouteOptions = {
  loader: ({ context }: { context: { queryClient: QueryClient; location?: { id: number } } }) => {
    warmQueries(context.queryClient, inboxQueries({ locationId: context.location?.id }));
    preloadMessaging();
  },
};

const warmChannel = (queryClient: QueryClient, channelUrl: string) =>
  void import("domains/messaging/containers/Channel/queries").then((m) => m.warmChannel(queryClient, channelUrl));

export function makeMessagingRoutes<TParent extends AnyRoute>(parentRoute: TParent) {
  const layout = createRoute({
    getParentRoute: () => parentRoute,
    id: "messaging",
    component: Home,
  });

  const index = createRoute({
    getParentRoute: () => layout,
    path: "/",
    staticData: { messagingDepth: 0, messagingListDepth: 0 },
    wrapInSuspense: false,
    component: ListView,
  });

  const channel = createRoute({
    getParentRoute: () => layout,
    path: "chat/$channelUrl",
    staticData: { messagingDepth: 1, messagingListDepth: 0 },
    wrapInSuspense: false,
    loader: ({ params, context, preload }) => preload && warmChannel(context.queryClient, params.channelUrl),
    component: ChannelView,
  });

  const group = createRoute({
    getParentRoute: () => layout,
    path: "shift/$groupId",
    staticData: { messagingDepth: 1, messagingListDepth: 1 },
    wrapInSuspense: false,
    component: ListView,
  });

  const groupChannel = createRoute({
    getParentRoute: () => layout,
    path: "shift/$groupId/chat/$channelUrl",
    staticData: { messagingDepth: 2, messagingListDepth: 1 },
    wrapInSuspense: false,
    loader: ({ params, context, preload }) => preload && warmChannel(context.queryClient, params.channelUrl),
    component: ChannelView,
  });

  return layout.addChildren([index, channel, group, groupChannel]);
}

// Only the standalone mount wraps <MessagingChatTokenProvider> (inboxes get their provider from their view).
// Lazy-loaded so the provider modules stay out of the eager bundle.
const chatsLayoutComponent = lazyRouteComponent(() =>
  authProvider().then((m) => ({
    default: function ChatsLayout() {
      return (
        <m.MessagingChatTokenProvider>
          <Outlet />
        </m.MessagingChatTokenProvider>
      );
    },
  })),
);

const chatsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "chats/$chatToken",
  validateSearch: z.object({ returnTo: z.optional(z.string()) }),
  loader: ({ params, context }) => {
    warmQueries(context.queryClient, chatTokenQueries({ chatToken: params.chatToken }));
    preloadMessaging();
  },
  component: chatsLayoutComponent,
});

const inboxRoute = createRoute({
  getParentRoute: () => locationLayoutRoute,
  path: "inbox",
  ...messagingInboxRouteOptions,
  component: lazyRouteComponent(() => import("views/Inbox"), "Inbox"),
});

export const messagingStandaloneRoutes = chatsRoute.addChildren([makeMessagingRoutes(chatsRoute)]);

export const messagingInboxRoutes = inboxRoute.addChildren([makeMessagingRoutes(inboxRoute)]);
